import { APP_BASE_HREF } from '@angular/common';
import { Component, Inject, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';

import { Debounce } from 'lodash-decorators';
import { Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { McpOauthService } from '../../services/mcp-oauth.service';

import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { ActionKeys } from '../../../Common/Forms/enums/keys.enum';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';

import { PartnerResellers } from '../../../Core/Config/enums/partner-resellers.enum';
import { ConfigService } from '../../../Core/Config/services/config.service';
import { MfaStatusModel } from '../../../Core/Users/models/mfa.model';

import { partnerResellerConfigs, ResellerConfig } from '../../config/resellers/reseller-config-model';
import { LoginInfo, LoginModel } from '../../models/login-info.model';
import { AuthenticationError } from '../../services/error-handler.service';
import { LoginResult, LoginService, LoginType } from '../../services/login.service';
import { MfaService } from '../../services/mfa.service';
import { SsoLoginComponent } from '../SsoLogin/sso-login.component';


export enum LoginErrorTypes {
    sessionLimit = 'SESSION_LIMIT_EXCEEDED',
}

@Component({
    selector: 'agpr-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent implements OnDestroy {
    forgotUrl: string = 'ForgotPassword';

    waitingForLogin: boolean = false;
    displayAuthError: boolean = false;
    displayRequiredError: boolean = false;
    displayMultiSessionError: boolean = false;
    displaySessionLimitError: boolean = false;
    displayNoSsoError: boolean = false;
    displayForbiddenError: boolean = false;

    lockedOut: boolean = false;
    lastUsername: string = '';
    failedAttempts: number = 0;

    loginInfo: LoginInfo = new LoginInfo();
    newPassword: string;
    newPasswordRetype: string;
    logoUrl: string;
    returnUrl: string;
    logoAltText: string;
    // Tracks the status of the agility login, it needs to be completed in different ways depending on what the next step is
    tosOrPassword: boolean = false;

    commonLoginForm: FormGroup;
    classicResetForm: FormGroup;

    isResellerDomain: boolean = false;
    partnerReseller: PartnerResellers;
    resellerConfig: ResellerConfig;
    paUrl: SafeResourceUrl;

    // True when this login page was reached as part of an MCP / Connector OAuth flow.
    isOauthFlow: boolean = false;

    readonly messageSeverityEnum = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly partnerResellerEnum = PartnerResellers;

    private subscriptions: Subscription[] = [];

    constructor(
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        private loginService: LoginService,
        private router: Router,
        private route: ActivatedRoute,
        private configService: ConfigService,
        private mfaService: MfaService,
        private mcpOauthService: McpOauthService,
    ) {

        this.commonLoginForm = new FormGroup({
            username: new FormControl<string>(null),
            password: new FormControl<string>(null),
            sso: new FormControl<boolean>(false)
        }, Validators.required);

        const routerState = this.router.currentNavigation()?.extras?.state;
        if (routerState?.username) {
            this.commonLoginForm.patchValue({
                username: routerState.username
            });
            this.displayNoSsoError = true;
        }

        this.subscriptions.push(
            this.route.queryParams.subscribe(
                queryParams => {
                    // Sometimes, the login page gets set as the return URL, we don't want to redirect to login on login, ignore that
                    // TODO: For now, we won't allow any relative URLs, these cause an issue when there are neptune only
                    //  and classic accounts being used at the same time, check for http, which also checks for https
                    if (
                        queryParams.returnUrl &&
                        queryParams.returnUrl.toLowerCase().indexOf('login') === -1 &&
                        queryParams.returnUrl.indexOf('http') !== -1
                    ) {
                        this.returnUrl = queryParams.returnUrl;
                    }
                }
            ),
            this.commonLoginForm.valueChanges.subscribe(value => {
                this.loginInfo.username = value.username?.trim();
                this.loginInfo.password = value.password;
            }),
        );

        if (this.configService.hasBrandGUID() && !this.configService.getNoRedirect()) {
            this.isResellerDomain = true;
            this.partnerReseller = this.configService.getPartnerReseller();
            this.resellerConfig = partnerResellerConfigs.get(this.partnerReseller);
            if (!!this.resellerConfig.logoUrl) {
                this.logoUrl = appBaseHref + this.resellerConfig.logoUrl;
            }
            if (!!this.resellerConfig.logoAltText) {
                this.logoAltText = this.resellerConfig.logoAltText;
            }
        } else {
            this.logoUrl = appBaseHref + 'assets/images/agility-logo-vertical.png';
            this.logoAltText = 'Agility PR Solutions';
        }

        // Detect an inbound MCP / Connector OAuth authorization request. The captured
        // context is replayed once the login below completes (see LoginService.postLogin).
        // The already-authenticated case is handled earlier, in LoginRouteGuard.
        this.isOauthFlow = this.mcpOauthService.captureContextFromUrl();
    }


    signIn(forceLogin: boolean = false) {
        if (!(this.loginInfo.username && this.loginInfo.password)) {
            this.displayRequiredError = true;
            return;
        }

        this.waitingForLogin = true;
        this.clearErrorMessages();

        this.lockedOut = false;
        if (this.lastUsername !== this.loginInfo.username) {
            this.failedAttempts = 0;
            this.lastUsername = this.loginInfo.username;
        }

        const loginModel: LoginModel = {
            loginInfo: {
                username: this.loginInfo.username,
                password: this.loginInfo.password,
            },
            forceLogin: forceLogin,
            returnUrl: this.returnUrl
        };
        this.loginService.performLogin(loginModel).pipe(
            catchError(
                (err) => {
                    this.waitingForLogin = false;
                    if (err instanceof AuthenticationError) {
                        this.displayAuthError = true;
                        this.failedAttempts++;
                    } else if (err?.status === 429) {
                        // Rate limit for failed login attempts
                        this.displayAuthError = true;
                        this.lockedOut = true;
                    }  else if (err?.status === 403) {
                        // Rate limit for failed login attempts
                        this.displayForbiddenError = true;
                    } else if (err instanceof MfaStatusModel) {
                        this.mfaService.setLoginInfo(this.loginInfo);
                        this.mfaService.setCurrentMfaStatus(err);
                        const navExtras: NavigationExtras = this.returnUrl ? { queryParams: { returnUrl: this.returnUrl }}: {};
                        this.router.navigate(['MultiFactorAuthentication'], navExtras);
                    } else if ((err?.status === 409) && (err?.error?.type === LoginErrorTypes.sessionLimit)) {
                        // Handle other device error
                        this.displaySessionLimitError = true;
                    }
                    throw err;
                }
            )
        ).subscribe((loginResult: LoginResult) => {
            if (loginResult.loginType === LoginType.MFA) {
                // first leg of sign in workflow
                this.mfaService.navigateToMfaCodePage(loginResult, loginModel);
                return;
            }
            if (loginResult.loginType === LoginType.SSO && loginResult.ssoResponse?.redirectUri) {
                const navExtras: NavigationExtras = {
                    queryParams: {
                        [SsoLoginComponent.SSO_LOGIN_REDIRECT_PARAM]: true
                    }
                };
                this.router.navigate(['ssoLogin'], navExtras);
                return;
            }
        });
    }

    clearErrorMessages() {
        this.displayAuthError = false;
        this.displayRequiredError = false;
        this.displayMultiSessionError = false;
        this.displaySessionLimitError = false;
        this.displayNoSsoError = false;
    }

    @Debounce(1000, {leading: false})
    onEnter(event) {
        if (event.key === ActionKeys.ENTER) {
            this.signIn();
        }
    }

    gotoForgotPassword() {
        this.router.navigate(['ForgotPassword']);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}


